/**
 * Create or edit a project — a port of `bimgo-ai-web`'s `ProjectModal` onto the
 * local `Modal` (we have no shadcn `Dialog`).
 *
 * Same shape as the web client's: one component for both modes, the same title
 * and blurb per mode, the same three fields (name required, description and
 * notes as textareas with a hint underneath), and the same button wording —
 * "Create Project"/"Update Project", becoming "Creating…"/"Updating…" while the
 * request is in flight.
 *
 * Validation matches too: the submit button stays enabled and an empty name is
 * answered with `projects.projectNameRequired` under the field, rather than a
 * dead button that never says why.
 *
 * The one departure is failure handling. The web client logs to `console.error`
 * and leaves the dialog open with no message; here the save is owned by the
 * caller and whatever the server said is shown, with the dialog closing only
 * once the write has actually succeeded.
 */
import { useEffect, useState, type FormEvent } from "react";
import { useLocale } from "../../i18n/LocaleProvider";
import { Modal } from "../settings/Modal";
import type { Project } from "../../lib/projects";
import { Spinner } from "../settings/Spinner";

export interface ProjectDraft {
  name: string;
  description: string;
  notes: string;
}

export function ProjectModal({
  open,
  mode,
  project,
  onClose,
  onSave,
}: {
  open: boolean;
  mode: "create" | "edit";
  /** The row being edited; ignored in create mode. */
  project?: Project | null;
  onClose: () => void;
  /** Resolves on success; throws with a message the user should see on failure. */
  onSave: (draft: ProjectDraft) => Promise<void>;
}) {
  const { t } = useLocale();
  const [draft, setDraft] = useState<ProjectDraft>({ name: "", description: "", notes: "" });
  const [saving, setSaving] = useState(false);
  const [nameError, setNameError] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Refill whenever the dialog opens, so editing a second row does not show the
  // first one's values, and creating after an edit starts empty.
  useEffect(() => {
    if (!open) return;
    setError(null);
    setNameError(null);
    setSaving(false);
    setDraft({
      name: mode === "edit" ? (project?.name ?? "") : "",
      description: mode === "edit" ? (project?.description ?? "") : "",
      notes: mode === "edit" ? (project?.notes ?? "") : "",
    });
  }, [open, mode, project]);

  async function submit(event: FormEvent) {
    event.preventDefault();
    if (saving) return;
    if (!draft.name.trim()) {
      setNameError(t("projects.projectNameRequired"));
      return;
    }
    setSaving(true);
    setNameError(null);
    setError(null);
    try {
      await onSave({
        name: draft.name.trim(),
        description: draft.description.trim(),
        notes: draft.notes.trim(),
      });
      onClose();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setSaving(false);
    }
  }

  const submitLabel = saving
    ? mode === "create"
      ? t("projects.creating")
      : t("projects.updating")
    : mode === "create"
      ? t("projects.createProject")
      : t("projects.updateProject");

  return (
    <Modal
      open={open}
      onClose={() => (saving ? undefined : onClose())}
      title={mode === "create" ? t("projects.createProject") : t("projects.editProject")}
      description={
        mode === "create" ? t("projects.createProjectDesc") : t("projects.editProjectDesc")
      }
      footer={
        <>
          <button
            type="button"
            className="settings-btn secondary"
            onClick={onClose}
            disabled={saving}
          >
            {t("projects.cancel")}
          </button>
          <button type="submit" form="project-form" className="settings-btn" disabled={saving}>
            {saving ? <Spinner size={14} /> : null}
            {submitLabel}
          </button>
        </>
      }
    >
      <form id="project-form" onSubmit={submit} noValidate>
        <div className="settings-field">
          <label className="auth-label" htmlFor="project-name">
            {t("projects.projectName")}
          </label>
          <input
            id="project-name"
            className="settings-input"
            style={{ width: "100%" }}
            placeholder={t("projects.projectNamePlaceholder")}
            value={draft.name}
            onChange={(e) => {
              setDraft((d) => ({ ...d, name: e.target.value }));
              if (nameError) setNameError(null);
            }}
            disabled={saving}
            autoFocus
          />
          {nameError ? (
            <p className="field-error" role="alert">
              {nameError}
            </p>
          ) : null}
        </div>

        <div className="settings-field">
          <label className="auth-label" htmlFor="project-description">
            {t("projects.projectDescription")}
          </label>
          <textarea
            id="project-description"
            className="settings-input settings-textarea"
            placeholder={t("projects.projectDescriptionPlaceholder")}
            value={draft.description}
            onChange={(e) => setDraft((d) => ({ ...d, description: e.target.value }))}
            disabled={saving}
          />
          <p className="field-hint">{t("projects.projectDescriptionHint")}</p>
        </div>

        <div className="settings-field">
          <label className="auth-label" htmlFor="project-notes">
            {t("projects.projectNotes")}
          </label>
          <textarea
            id="project-notes"
            className="settings-input settings-textarea"
            placeholder={t("projects.projectNotesPlaceholder")}
            value={draft.notes}
            onChange={(e) => setDraft((d) => ({ ...d, notes: e.target.value }))}
            disabled={saving}
          />
          <p className="field-hint">{t("projects.projectNotesHint")}</p>
        </div>

        {error ? (
          <p className="settings-msg err" role="alert">
            {error}
          </p>
        ) : null}
      </form>
    </Modal>
  );
}
